import React, { useState } from "react";
import { useMediaQuery } from "@mui/material";
import { AppBar, Avatar, IconButton, Toolbar, Typography, TextField, InputAdornment, Box } from "@mui/material";
import menuIcon from "../../assets/icons/menubar.png";
import bellIcon from "../../assets/icons/bell.png";
import SearchIcon from "@mui/icons-material/Search";
import Sidebar from "./Sidebar";
import { ProfilePopup } from "./AvatarOverlay";
import NotificationOverlay from "./NotificationOverlay";
import OverlayModal from "../User/OverlayModal";
import Profile from "../../pages/User/Profile";
import Settings from "../../pages/User/Settings";
import Support from "../../pages/User/Support";
import FAQ from "../../pages/User/FAQ";
import userImage from "../../assets/images/user.png";
import { useSidebar } from "../SidebarContext";
import { useUserProfile } from "../../hooks/useUserProfile";

const Header = ({ setSearchQuery = () => {}, setSortOrder = () => {}, title = "UFind" }) => {
  const sidebarContext = useSidebar();
  const { isSidebarOpen = false, toggleSidebar = () => {} } = sidebarContext || {};
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const isMobile = useMediaQuery("(max-width:600px)");
  
  // Get user data from custom hook
  const { photoURL } = useUserProfile();

  const closeAllPopups = () => {
    setIsProfileOpen(false);
    setIsNotificationOpen(false);
  };

  const toggleProfilePopup = () => {
    closeAllPopups();
    setIsProfileOpen(!isProfileOpen);
  };

  const toggleNotificationOverlay = () => {
    closeAllPopups();
    setIsNotificationOpen(!isNotificationOpen);
  };

  // Overlay modal open handler
  const openOverlayModal = (contentType, options = {}) => {
    switch (contentType) {
      case "profile":
        setModalContent(<Profile />);
        break;
      case "settings":
        setModalContent(<Settings initialTab={options.tab} />);
        break;
      case "support":
        setModalContent(<Support />);
        break;
      case "faq":
        setModalContent(<FAQ />);
        break;
      default:
        setModalContent(null);
    }
    setModalOpen(true);
  };

  const closeOverlayModal = () => {
    setModalOpen(false);
    setModalContent(null);
  };

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={0} 
        sx={{ 
          bgcolor: "#fbffe8",
          top: 0,
          zIndex: 1100,
          height: isMobile ? 56 : 64
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 56, sm: 80 },
            px: { xs: 2, sm: 4 },
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {!isSidebarOpen && (
            <IconButton 
              edge="start" 
              color="inherit" 
              aria-label="menu" 
              sx={{ 
                mr: 2,
                display: 'flex',
                alignItems: 'center',
                padding: { xs: '6px', sm: '8px' },
                zIndex: 1300
              }} 
              onClick={toggleSidebar}
            >
              <img 
                src={menuIcon} 
                alt="Menu" 
                style={{ 
                  width: isMobile ? 28 : 32, 
                  height: isMobile ? 28 : 32,
                  display: 'block'
                }} 
              />
            </IconButton>
          )}
          <Typography
            variant="h5"
            sx={{
              fontFamily: "Poppins, Helvetica",
              fontWeight: 700,
              color: "#516d4f",
              fontSize: { xs: "20px", sm: "24px" },
              letterSpacing: 0.5,
              mr: 1,
              display: 'flex',
              alignItems: 'center',
              height: isMobile ? '35px' : '40px'
            }}
          >
            {title}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {/* Search input */}
          <TextField
            placeholder="Search..."
            size="small"
            variant="outlined"
            sx={{
              bgcolor: "#aac093",
              borderRadius: "30px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "30px",
                pl: 2,
                pr: 1,
              },
              "& fieldset": { border: "none" },
              width: 200,
              mr: 2,
            }}
            InputProps={{
              sx: { color: "white", height: 45 },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "white" }} />
                </InputAdornment>
              ),
            }}
            onFocus={() => {
              closeAllPopups();
            }}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
          {/* Notification button */}
          <IconButton color="inherit" aria-label="notifications" sx={{ mr: 2 }} onClick={toggleNotificationOverlay}>
            <img src={bellIcon} alt="Notifications" style={{ width: isMobile ? 30 : 35, height: isMobile ? 30 : 35 }} />
          </IconButton>
          {/* Profile button */}
          <IconButton edge="end" color="inherit" aria-label="profile" onClick={toggleProfilePopup}>
            <Avatar alt="User Profile" src={photoURL || userImage} sx={{ width: isMobile ? 35 : 40, height: isMobile ? 35 : 40 }} />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      {/* Profile Popup */}
      {isProfileOpen && (
        <>
          {/* Backdrop overlay */}
          <div
            onClick={() => setIsProfileOpen(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
              backgroundColor: "transparent",
            }}
          />
          {/* Popup */}
          <div
            style={{
              position: "absolute",
              top: "60px",
              right: "20px",
              zIndex: 1000,
            }}
          >
            <ProfilePopup openOverlayModal={openOverlayModal} />
          </div>
        </>
      )}

      {/* Overlay Modal for Profile/Settings/Support/FAQ */}
      <OverlayModal open={modalOpen} onClose={closeOverlayModal}>
        {modalContent}
      </OverlayModal>

      {/* Notification Overlay */}
      {isNotificationOpen && (
        <div
          style={{
            position: "absolute",
            top: "60px",
            right: "60px",
            zIndex: 1000,
          }}
        >
          <NotificationOverlay onClose={toggleNotificationOverlay} />
        </div>
      )}
    </>
  );
};

export default Header;